import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Pressable, Platform, StyleSheet } from 'react-native';
import * as Clipboard from 'expo-clipboard';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useToastStore } from '@/store';

const COPY_FEEDBACK_MS = 2000;

async function copyTextToClipboard(text) {
  if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  await Clipboard.setStringAsync(text);
}

function PromptRecipeBlock({ systemPrompt = '', promptTemplate = '' }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { addToast } = useToastStore(s => s.actions);
  const [expanded, setExpanded] = useState(true);
  const [copied, setCopied] = useState(false);
  const copiedTimerRef = useRef(null);

  useEffect(() => () => {
    if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
  }, []);

  const recipeText = useMemo(() => {
    const parts = [];
    if (systemPrompt?.trim()) parts.push(systemPrompt.trim());
    if (promptTemplate?.trim() && promptTemplate.trim() !== systemPrompt?.trim()) {
      parts.push(`Template:\n${promptTemplate.trim()}`);
    }
    return parts.join('\n\n');
  }, [systemPrompt, promptTemplate]);

  const styles = useMemo(() => StyleSheet.create({
    wrap: {
      borderRadius: 14,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
      overflow: 'hidden',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderBottomWidth: expanded ? 1 : 0,
      borderBottomColor: colors.border_color_secondary,
      gap: 12,
    },
    titlePress: {
      flex: 1,
      minWidth: 0,
      ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    },
    actions: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      flexShrink: 0,
    },
    copyBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    },
    chevronBtn: {
      padding: 4,
      ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    },
    body: {
      padding: 16,
    },
    pre: {
      fontFamily: Platform.OS === 'web' ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : undefined,
      fontSize: 13,
      lineHeight: 20,
      color: colors.text_color_secondary,
    },
  }), [colors, expanded]);

  const handleCopy = useCallback(async () => {
    if (!recipeText) return;
    try {
      await copyTextToClipboard(recipeText);
      setCopied(true);
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
    } catch {
      addToast(t('pages.prompts.recipeCopyFailed'), 'error');
    }
  }, [recipeText, addToast, t]);

  if (!recipeText) return null;

  const copyLabel = copied
    ? (t('common.copied') || 'Copied!')
    : (t('common.copy') || 'Copy');

  return (
    <View style={styles.wrap}>
      <View style={styles.header}>
        <Pressable
          onPress={() => setExpanded(v => !v)}
          style={styles.titlePress}
          accessibilityRole="button"
          accessibilityLabel={t('pages.prompts.recipeTitle') || 'Recipe'}
        >
          <Text weight="bold" size="s">
            {t('pages.prompts.recipeTitle') || 'Recipe'}
          </Text>
        </Pressable>
        <View style={styles.actions}>
          <Pressable
            onPress={handleCopy}
            style={styles.copyBtn}
            accessibilityRole="button"
            accessibilityLabel={copyLabel}
          >
            <Icon name={copied ? 'check' : 'copy'} size={12} color={colors.text_color_secondary} />
            <Text size="xs" color="secondary">
              {copyLabel}
            </Text>
          </Pressable>
          <Pressable
            onPress={() => setExpanded(v => !v)}
            style={styles.chevronBtn}
            accessibilityRole="button"
            accessibilityLabel={expanded ? 'Collapse recipe' : 'Expand recipe'}
          >
            <Icon
              name={expanded ? 'chevron-up' : 'chevron-down'}
              size={12}
              color={colors.text_color_tertiary}
            />
          </Pressable>
        </View>
      </View>
      {expanded ? (
        <View style={styles.body}>
          <Text style={styles.pre} selectable>
            {recipeText}
          </Text>
        </View>
      ) : null}
    </View>
  );
}

export default React.memo(PromptRecipeBlock);
